import { ALL_USER_DOCUMENT_CATEGORIES_QUERY } from '@/modules/users/queries'
import type { AllUserDocumentCategoriesQuery } from '@/modules/users/types.graphql'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

interface CommonProps extends ComboboxCommonProps<string> {
  clearable?: boolean
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function UserDocumentCategoryCombobox({
  mode,
  value,

  placeholder,
  searchPlaceholder,
  noResultsText,
  clearable = true,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    loading: 'common.loading',
    placeholder: 'common.select-ellipsis',
  })

  const { data, loading } = useQuery<AllUserDocumentCategoriesQuery>(
    ALL_USER_DOCUMENT_CATEGORIES_QUERY
  )

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      (data?.allUserDocumentCategories.edges ?? []).map(({ node }) => ({
        value: node.id,
        label: node.name,
      })),
    [data?.allUserDocumentCategories.edges]
  )

  const resolvedPlaceholder = loading
    ? translations.loading
    : placeholder ?? translations.placeholder

  if (mode === 'single') {
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={items}
        clearable={clearable}
        placeholder={resolvedPlaceholder}
        searchPlaceholder={searchPlaceholder}
        noResultsText={noResultsText}
        onChange={onChange}
      />
    )
  }

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={items}
      placeholder={resolvedPlaceholder}
      searchPlaceholder={searchPlaceholder}
      noResultsText={noResultsText}
      onChange={onChange}
    />
  )
}
